import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { vscodeTheme } from '../../styles/theme';

export interface SearchableSelectItem {
  id: string;
  label: string;
}

interface SearchableSelectProps {
  label?: string;
  items: SearchableSelectItem[];
  selectedId?: string | null;
  onChange: (id: string) => void;
  placeholder?: string;
  filterText: string;
  onFilterTextChange: (value: string) => void;
  emptyText?: string;
  renderHeader?: () => ReactNode;
  renderItemActions?: (item: SearchableSelectItem) => ReactNode;
  renderFooter?: () => ReactNode;
}

export function SearchableSelect({
  label,
  items,
  selectedId,
  onChange,
  placeholder,
  filterText,
  onFilterTextChange,
  emptyText = 'No items found',
  renderHeader,
  renderItemActions,
  renderFooter,
}: SearchableSelectProps) {
  const [isFocused, setIsFocused] = useState(false);

  const selectedLabel = useMemo(
    () => items.find((item) => item.id === selectedId)?.label,
    [items, selectedId]
  );
  const filteredItems = useMemo(
    () =>
      items.filter((item) =>
        item.label.toLowerCase().includes(filterText.toLowerCase())
      ),
    [items, filterText]
  );
  const resolvedPlaceholder = placeholder ?? selectedLabel ?? 'Filter...';

  return (
    <div>
      {label && (
        <label
          style={{
            color: vscodeTheme.colors.textSecondary,
            fontSize: '11px',
            fontFamily: vscodeTheme.typography.fontFamily,
            display: 'block',
            marginBottom: '4px',
          }}
        >
          {label}
        </label>
      )}
      <input
        type="text"
        placeholder={resolvedPlaceholder}
        value={filterText}
        onChange={(e) => onFilterTextChange(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setTimeout(() => setIsFocused(false), 200)}
        style={{
          width: '100%',
          padding: '6px 8px',
          backgroundColor: vscodeTheme.colors.editorBackground,
          color: vscodeTheme.colors.textPrimary,
          border: `1px solid ${vscodeTheme.colors.border}`,
          borderRadius: '2px',
          fontSize: vscodeTheme.typography.fontSize,
          fontFamily: vscodeTheme.typography.fontFamily,
          marginBottom: '6px',
        }}
      />
      {isFocused && (
        <div
          style={{
            border: `1px solid ${vscodeTheme.colors.border}`,
            borderRadius: '2px',
            overflow: 'hidden',
            marginTop: '4px',
          }}
          onMouseDown={(event) => event.preventDefault()}
        >
          {renderHeader ? renderHeader() : null}
          {filteredItems.length === 0 && !renderFooter && (
            <div
              style={{
                padding: '6px 8px',
                color: vscodeTheme.colors.textSecondary,
                fontSize: vscodeTheme.typography.fontSize,
                fontFamily: vscodeTheme.typography.fontFamily,
              }}
            >
              {emptyText}
            </div>
          )}
          {filteredItems.map((item) => {
            const isSelected = item.id === selectedId;
            return (
              <div
                key={item.id}
                onClick={() => {
                  onChange(item.id);
                  setIsFocused(false);
                }}
                style={{
                  padding: '6px 8px',
                  cursor: 'pointer',
                  backgroundColor: isSelected
                    ? vscodeTheme.colors.activeBackground
                    : 'transparent',
                  color: vscodeTheme.colors.textPrimary,
                  fontSize: vscodeTheme.typography.fontSize,
                  fontFamily: vscodeTheme.typography.fontFamily,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '8px',
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = isSelected
                    ? vscodeTheme.colors.activeBackground
                    : vscodeTheme.colors.hoverBackground)
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = isSelected
                    ? vscodeTheme.colors.activeBackground
                    : 'transparent')
                }
              >
                <span>{item.label}</span>
                {renderItemActions ? renderItemActions(item) : null}
              </div>
            );
          })}
          {renderFooter ? renderFooter() : null}
        </div>
      )}
    </div>
  );
}
